import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { getOne } from '@/shared/api/http';
import { isCurrentUser } from '@/shared/api/generated/tenancy';
import type { CurrentUser } from '@/shared/api/generated/tenancy';

/* Who the caller is, and what they may do — one request, read by every screen that gates a control.
 *
 * **In `entities/` rather than in a feature**, because it is not a feature's data: the shell reads
 * it to gate the nav, the Admin screens read it to gate their controls, and any screen that hides a
 * button reads it. A feature that owned it would be one every other feature had to import from,
 * which is the coupling the layer split exists to prevent.
 *
 * `/v1/auth/me` is the one deliberately ungated read on the platform — see the route's own note in
 * `lumiq-arch`'s `UNGATED_READ_PATHS`. It has to be: a subject holding no roles must still be able
 * to discover that they hold none, which is the state of every freshly invited teammate and exactly
 * the answer the screen needs in order to say so.
 */

export const IDENTITY_URL = '/v1/auth/me';
export const IDENTITY_QUERY_KEY = ['auth', 'me'] as const;

export function useIdentity(): UseQueryResult<CurrentUser> {
  return useQuery({
    queryKey: IDENTITY_QUERY_KEY,
    queryFn: () => getOne(IDENTITY_URL, isCurrentUser, 'current user'),
    /* No retry: a 401 here means the session is gone, and `getOne` has already cleared the token.
     * Retrying would delay the redirect to the login screen for no benefit. */
    retry: false,
    /* Roles change rarely and every screen reads this, so a short cache avoids one request per
     * navigation. Not infinite: an administrator who revokes a role should see the nav change
     * without a reload, and the backend refuses regardless of what the browser believes. */
    staleTime: 60_000,
  });
}
